import React from 'react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import PageHeader from '../components/PageHeader';
import { imageUrl } from '../lib/images';

export default function Multimodal() {
  return (
    <>
      <SEO
        title="Multimodal Transport | Fastlane ApS"
        description="Multimodaltransport, Grøntransport eller Kombineret Transport. En godstransport som er en kombination mellem vej, bane og skib."
        canonicalUrl="https://fastlane.dk/fragt-kombineret-transport"
      />

      <PageHeader
        title="Multimodal Transport"
        subtitle="Grøn og effektiv godstransport – kombination af lastbil, tog og skib i hele Europa og oversøisk."
        breadcrumbs={[
          { label: 'Ydelser', path: '/godstransport-i-europa' },
          { label: 'Multimodal Transport' }
        ]}
      />

      <div className="py-12 sm:py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-12">
          {/* Main intro */}
          <div className="space-y-4 text-slate-700 leading-relaxed text-base sm:text-lg">
            <h2 className="text-2xl font-bold text-[#0f2b48]">
              Multimodaltransport, Grøntransport eller Kombineret Transport
            </h2>
            <p>
              En godstransport som er en kombination mellem flere transportformer kaldes også multimodal transport. Fragtgods hentes typisk med en lastbil, ligeså leveres fragtgods typisk med en lastbil. Derfor er det ikke synligt for de fleste transportkøbere når deres gods har været ude at køre f.eks. med tog eller er blevet sendt med et RORO (roll on roll of) skib.
            </p>
            <p>
              Internationalt fragtes gods med bane og skib i stor stil. Godset læsses i Huckepack trailere der kan læsses på og af et tog eller trailerne sendes løse (uden chaufføre) med RORO skib/færge.
            </p>
          </div>

          {/* Scrape Photos */}
          <div className="flex flex-wrap gap-6 items-start">
            <div className="w-fit max-w-full rounded-xl overflow-hidden shadow-xs border border-slate-200 bg-slate-100">
              <img
                src={imageUrl('https://assets.auxodev.dk/assets/2e6d0d1c-821d-400c-80aa-275c6c0c86cc/dfds-truck-magnolia-seaways.jpg', 'width=800,quality=85,format=auto')}
                alt="RORO skib til transport af løstrailere shortsea"
                className="max-h-[350px] w-auto max-w-full object-cover block"
              />
              <p className="w-full text-xs text-slate-500 p-3 bg-slate-50 border-t border-slate-100 text-left">
                RORO skib til transport af løstrailere shortsea
              </p>
            </div>
            <div className="w-fit max-w-full rounded-xl overflow-hidden shadow-xs border border-slate-200 bg-slate-100">
              <img
                src={imageUrl('https://assets.auxodev.dk/assets/2e6d0d1c-821d-400c-80aa-275c6c0c86cc/137y0293-2-0.jpg', 'width=800,quality=85,format=auto')}
                alt="Sib med containere og løstrailere shortsea"
                className="max-h-[350px] w-auto max-w-full object-cover block"
              />
              <p className="w-full text-xs text-slate-500 p-3 bg-slate-50 border-t border-slate-100 text-left">
                Skib med containere og løstrailere shortsea
              </p>
            </div>
          </div>

          {/* Ruter og Netværk */}
          <div className="space-y-4 text-slate-700 leading-relaxed text-sm sm:text-base">
            <p>
              RORO skibe og færger forbinder kystområderne i Europa og øerne via skibsnetværk i kombination med banetransport og vejtransport:
            </p>
            <ul className="list-disc pl-5 space-y-1.5 text-slate-700">
              <li>mellem Europa/Skandinavien og England/Irland</li>
              <li>mellem Europa og Baltikum / Østersøregionen</li>
              <li>mellem Europa og den norske vestkyst, Færøerne, Island og Grønland</li>
              <li>mellem Middelhavsregionen og Europa</li>
              <li>mellem Tyrkiet og Europa</li>
              <li>mellem Midteuropas floder og resten af Europa</li>
            </ul>
            <p className="pt-2 font-medium">
              Transporten består af 2 el. flere transportformer og kaldes multimodal eller kombineret transport.
            </p>
          </div>

          {/* Fordele */}
          <div className="bg-emerald-50/70 p-6 sm:p-8 rounded-2xl border border-emerald-200/80 space-y-4">
            <h3 className="text-xl font-bold text-emerald-950 flex items-center gap-2">
              <i className="fa-solid fa-leaf text-emerald-700" aria-hidden="true" />
              <span>Fordele ved at kombinere vejtransport med tog og skib er mange:</span>
            </h3>
            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm text-emerald-900">
              <li className="flex items-start gap-2">
                <i className="fa-solid fa-circle-check text-emerald-600 mt-1 shrink-0 text-xs" aria-hidden="true" />
                <span>mindre CO2 aftryk</span>
              </li>
              <li className="flex items-start gap-2">
                <i className="fa-solid fa-circle-check text-emerald-600 mt-1 shrink-0 text-xs" aria-hidden="true" />
                <span>højere nyttelast per trailer - op til 29 tons</span>
              </li>
              <li className="flex items-start gap-2">
                <i className="fa-solid fa-circle-check text-emerald-600 mt-1 shrink-0 text-xs" aria-hidden="true" />
                <span>mindre trafik på vejene</span>
              </li>
              <li className="flex items-start gap-2">
                <i className="fa-solid fa-circle-check text-emerald-600 mt-1 shrink-0 text-xs" aria-hidden="true" />
                <span>gode fragtpriser på hele læs og på lange strækninger</span>
              </li>
              <li className="flex items-start gap-2">
                <i className="fa-solid fa-circle-check text-emerald-600 mt-1 shrink-0 text-xs" aria-hidden="true" />
                <span>uafhængig af kørselsforbud som kan være gældende for vejnettet</span>
              </li>
              <li className="flex items-start gap-2">
                <i className="fa-solid fa-circle-check text-emerald-600 mt-1 shrink-0 text-xs" aria-hidden="true" />
                <span>flexibilitet i lastning og losning af trailerne, idet de sendes som er løstrailere</span>
              </li>
              <li className="flex items-start gap-2">
                <i className="fa-solid fa-circle-check text-emerald-600 mt-1 shrink-0 text-xs" aria-hidden="true" />
                <span>kombinationen med bane giver ca. 50% lavere udledning af CO2 og andre skadelige stoffer</span>
              </li>
              <li className="flex items-start gap-2">
                <i className="fa-solid fa-circle-check text-emerald-600 mt-1 shrink-0 text-xs" aria-hidden="true" />
                <span>kombinationen med skib giver ca. 75 % lavere udledning af CO2 og andre skadelige stoffer</span>
              </li>
            </ul>
          </div>

          {/* Silkevejen */}
          <div className="space-y-3 text-slate-700 leading-relaxed text-sm sm:text-base border-t border-slate-200 pt-8">
            <h2 className="text-xl font-bold text-[#0f2b48]">"Silkevejen"</h2>
            <p>
              I de senere år er der oprustet på togtrafikken fra Kina - Silkevejen. Det betyder, at man i dag har muligheden for at sende gods med tog fra Kina til Europa. Løsningen ligger prismæssigt og tidsmæssigt mellem søfagt og luftfragt og det er et fint alternativ. Typisk køres godset i almindelige 40 ft high cube container som vi kender det fra oversøiske skibstransporter. Der kan sendes både full container loads - FCL eller less than container load - LCL.
            </p>
          </div>

          {/* Overseas */}
          <div className="space-y-3 text-slate-700 leading-relaxed text-sm sm:text-base border-t border-slate-200 pt-8">
            <h2 className="text-xl font-bold text-[#0f2b48]">Overseas</h2>
            <p>
              Overseas, hvor skib eller fly er en nødvendighed for at krydse havene er per definition en mulitmodal transport fordi man er nød til at bruge skib eller fly i kombination med minimum vejtransport for afhentning og levering i begge ender af transporten. Søfragten kombineres også tit med transport på bane specielt i større lande, hvor containere sendes fra områder inde i landet og ud til de store havne.
            </p>
          </div>

          {/* Summary & Contact */}
          <div className="space-y-4 text-slate-700 leading-relaxed text-sm sm:text-base border-t border-slate-200 pt-8">
            <h2 className="text-xl font-bold text-[#0f2b48]">Multimodal-, kombineret- el. grøn transport</h2>
            <p>
              Kært barn har mange navne. Grøn transport er på sin plads, idet kombinationen med bane giver ca. 50% lavere udledning af CO2 og andre skadelige stoffer. Kombinationen med skib giver ca. 75 % lavere udledning af CO2 og andre skadelige stoffer. Fastlane sammensætter via vores database af udvalgte leverandører den transportløsning, der passer præcis til dit behov.
            </p>
            <p className="pt-2">
              Ønsker du den rigtige fragtpris på godstransport som en kombination af flere fragtformer i Europa eller overseas{' '}
              <a href="mailto:booking@fastlane.dk" className="text-sky-700 font-semibold underline">
                e-mail os
              </a>
              , ring til os (
              <a href="tel:+4576121230" className="font-semibold text-slate-900 whitespace-nowrap">
                +45 76 12 12 30
              </a>
              ) eller{' '}
              <Link to="/fragtrater" className="text-sky-700 font-semibold underline">
                send forespørgsel
              </Link>
              .
            </p>
          </div>
        </div>
      </div>
    </>
  );
}
